import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Input } from '@/components/ui/Input';
import { InlineCreateAccountModal, type CreatedAccount } from '@/components/InlineCreateAccountModal';
import { cn } from '@/lib/cn';

interface Account {
  id: string;
  name: string;
  industry?: string | null;
  sizeBucket?: string | null;
}

interface Props {
  value: string | null;
  /**
   * Called whenever the user picks a different account or clears it.
   * The optional `accountName` is provided so the parent can also display
   * the human-readable name without making another fetch.
   */
  onChange: (accountId: string | null, accountName?: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * When provided, used as the visible label when no value is selected
   * yet (e.g. for the lead conversion form, where we know the lead's
   * staging companyName but not its account_id).
   */
  hint?: string | null;
  className?: string;
  /**
   * Callback fired after a new account is created inline. Useful when the
   * parent wants to also do something with the just-created account
   * beyond just setting its id (e.g. update local list state).
   */
  onCreated?: (account: CreatedAccount) => void;
}

const SEARCH_DEBOUNCE_MS = 200;
const MAX_RESULTS = 25;

export function AccountCombobox({
  value,
  onChange,
  placeholder,
  disabled,
  hint,
  className,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Account[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve the label for a value passed in from the parent so the
  // collapsed input shows the human-readable name. Skips the fetch
  // when the parent already pushed the name via onChange.
  useEffect(() => {
    if (!value) {
      setSelectedLabel(null);
      return;
    }
    if (selectedLabel) return;
    apiClient
      .get<Account>(`/accounts/${value}`)
      .then(({ data }) => setSelectedLabel(data.name))
      .catch(() => setSelectedLabel(null));
  }, [value, selectedLabel]);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => {
      const params = query.trim()
        ? `?search=${encodeURIComponent(query.trim())}`
        : '';
      apiClient
        .get<Account[]>(`/accounts${params}`)
        .then(({ data }) => setResults(data.slice(0, MAX_RESULTS)))
        .catch(() => setResults([]));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, open]);

  function pickAccount(a: Account) {
    onChange(a.id, a.name);
    setSelectedLabel(a.name);
    setQuery('');
    setOpen(false);
  }

  function handleCreated(a: CreatedAccount) {
    pickAccount(a);
    setCreateOpen(false);
    onCreated?.(a);
  }

  function clear() {
    onChange(null, null);
    setSelectedLabel(null);
    setQuery('');
  }

  const displayValue = open ? query : (selectedLabel ?? '');
  const placeholderText = placeholder ?? t('accountCombobox.placeholder');
  const trimmedQuery = query.trim();
  const exactMatch = results.find(
    (a) => a.name.toLowerCase() === trimmedQuery.toLowerCase(),
  );
  const showCreate = open && trimmedQuery.length > 0 && !exactMatch;

  return (
    <div className={cn('relative', className)}>
      <Input
        value={displayValue}
        placeholder={hint ? `${placeholderText} (${hint})` : placeholderText}
        disabled={disabled}
        onFocus={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          setOpen(true);
        }}
        onBlur={() => {
          // Delay closing so a click on a result registers first.
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {value && !open && !disabled && (
        <button
          type="button"
          onClick={clear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={t('accountCombobox.clear')}
        >
          ×
        </button>
      )}
      {open && (
        <div className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {trimmedQuery
                ? t('accountCombobox.no_results')
                : t('accountCombobox.start_typing')}
            </div>
          )}
          {results.map((a) => (
            <button
              key={a.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickAccount(a)}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <div className="font-medium">{a.name}</div>
              {(a.industry || a.sizeBucket) && (
                <div className="text-xs text-muted-foreground">
                  {[a.industry, a.sizeBucket].filter(Boolean).join(' · ')}
                </div>
              )}
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setCreateOpen(true)}
              className="block w-full border-t px-3 py-2 text-left text-sm font-medium text-primary hover:bg-accent hover:text-accent-foreground"
            >
              + {t('accountCombobox.create_new', { name: trimmedQuery })}
            </button>
          )}
        </div>
      )}
      <InlineCreateAccountModal
        open={createOpen}
        initialName={trimmedQuery}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
